import React, { useCallback, useState } from 'react';
import { usePlacesWidget } from 'react-google-autocomplete';
import PropTypes from 'prop-types';
import Input from 'components/shared/input';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/shared/button';
import styles from './styles.module.scss';
import { api, GOOGLE_MAPS_API_KEY } from '../utils';
import { validationSchema } from './utils';
import Modal from '../components/Modal';
import useCaptcha from '../useCaptcha';

const RentGptAddNewProperty = ({ boxId = undefined }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { fetchCaptchaToken } = useCaptcha();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { ref } = usePlacesWidget({
    apiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
    componentRestrictions: { country: 'us' },
    options: {
      types: ['geocode', 'establishment'],
    },
    onPlaceSelected: (place) => {
      setValue('propertyAddress', place.formatted_address?.replace(', USA', ''), {
        shouldValidate: true,
      });
    },
  });

  const onSubmit = async ({
    websiteUrl,
    propertyName,
    propertyAddress,
    fullName,
    email,
    phone,
    propertyType,
    company,
  }) => {
    setIsLoading(true);
    const captchaToken = await fetchCaptchaToken();
    try {
      await api.post(
        '/properties',
        {
          name: propertyName,
          address: propertyAddress,
          url: websiteUrl,
          leadEmail: email,
          leadName: fullName,
          leadPhoneNumber: phone,
          property_type: propertyType,
          company_name: company,
        },
        {
          headers: {
            recaptcharesponse: captchaToken || '',
          },
        }
      );
      setShowModal(true);
      setTimeout(() => {
        setShowModal(false);
      }, 6000);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleModal = useCallback(() => {
    setShowModal((prev) => !prev);
  }, []);

  return (
    <section id={boxId} className={styles.root}>
      <header>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        <h3>Register Your Property for Swiftlane's RentGPT</h3>
        {/* eslint-disable-next-line react/no-unescaped-entities */}
        <p>Please fill the form below to start with Swiftlane's Leasing Bot</p>
      </header>
      <div className={styles.formBox}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="websiteUrl">Website URL</label>
              <Input
                {...register('websiteUrl')}
                placeholder="Please enter Website URL"
                withBorder
                requiredStyle
                error={errors.websiteUrl?.message}
                disabled={isLoading}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="propertyName">Property Name</label>
              <Input
                {...register('propertyName')}
                placeholder="Please enter Property Name"
                withBorder
                requiredStyle
                error={errors.propertyName?.message}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="propertyType">Property Type</label>
              <div className={styles.selectBox}>
                <select {...register('propertyType')} defaultValue="" disabled={isLoading}>
                  <option value="Multifamily">Multifamily</option>
                  <option value="Multifamily Community">Multifamily Community</option>
                  <option value="Community">Community</option>
                  <option value="Condominium">Condominium</option>
                  <option value="Apartment">Apartment</option>
                  <option value="Senior Housing">Senior Housing</option>
                  <option value="Student Housing">Student Housing</option>
                  <option value="Office">Office</option>
                  <option value="Retail">Retail</option>
                  <option value="Mixed Use">Mixed Use</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {errors.propertyType?.message}
            </div>
            <div className={styles.field}>
              <label htmlFor="propertyAddress">Property Address</label>
              <Controller
                control={control}
                name="propertyAddress"
                render={({ field }) => (
                  <Input
                    {...field}
                    ref={(e) => {
                      if (typeof ref === 'function') {
                        ref(e);
                      } else if (ref != null && typeof ref === 'object' && 'current' in ref) {
                        ref.current = e;
                      }
                      field.ref(e);
                    }}
                    placeholder="Please enter Property Address"
                    withBorder
                    requiredStyle
                    error={errors.propertyAddress?.message}
                    disabled={isLoading}
                    onChange={(e) => {
                      setValue('propertyAddress', e.target.value, { shouldValidate: true });
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className={styles.separator} />
          <h4 className={styles.contactInfoTitle}>Contact information</h4>
          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="fullName">Full Name</label>
              <Input
                {...register('fullName')}
                placeholder="Please enter Full Name"
                withBorder
                requiredStyle
                error={errors.fullName?.message}
                disabled={isLoading}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="email">Email</label>
              <Input
                {...register('email')}
                type="email"
                placeholder="Please enter Email"
                withBorder
                requiredStyle
                error={errors.email?.message}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label htmlFor="phone">Phone Number</label>
              <Input
                {...register('phone')}
                placeholder="Please enter Phone Number"
                withBorder
                requiredStyle
                error={errors.phone?.message}
                disabled={isLoading}
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="company">Company</label>
              <Input
                {...register('company')}
                placeholder="Please enter Company"
                withBorder
                requiredStyle
                error={errors.company?.message}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className={styles.buttons}>
            <Button type="submit" theme="accent-primary" loading={isLoading}>
              Submit
            </Button>
          </div>
        </form>
      </div>
      {showModal && (
        <Modal
          onClosePress={toggleModal}
          title="Thanks for signing up with RentGPT!"
          message="We will reach out to you soon with next steps to embed the chatbot on your website."
        />
      )}
    </section>
  );
};

RentGptAddNewProperty.propTypes = {
  boxId: PropTypes.string,
};

export default RentGptAddNewProperty;
